import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const COMPLETED_STATUSES = ['approved', 'do_generated', 'in_transit', 'completed', 'delivered'];

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtMT = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' MT';

const SummaryCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border" style={{ borderColor: '#fde68a' }}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-medium mb-1" style={{ color: '#6b7280' }}>{label}</p>
        <p className="text-xl font-bold" style={{ color: color || '#1f2937' }}>{value}</p>
        {sub && <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` || '#fef3c7' }}>
        <Icon name={icon} size={20} color={color || '#d97706'} />
      </div>
    </div>
  </div>
);

const ReportsCenterQMTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [datePreset, setDatePreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('id, buyer_email, factory_name, grade, quantity, rate_per_ton, total_amount, tds_applicable, tds_amount, net_payable, status, created_at, readable_booking_id, booking_type')
        ?.in('status', COMPLETED_STATUSES)
        ?.order('created_at', { ascending: false });
      if (!error && data) setBookings(data);
      setLoading(false);
    };
    fetchBookings();
  }, []);

  const filteredBookings = useMemo(() => {
    const { from, to } = getDateRange(datePreset, customFrom, customTo);
    const q = searchTerm.trim().toLowerCase();
    return bookings.filter(b => {
      if (q && !b.buyer_email?.toLowerCase().includes(q) && !b.factory_name?.toLowerCase().includes(q) && !b.grade?.toLowerCase().includes(q)) return false;
      if (from || to) {
        const d = new Date(b.created_at);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [bookings, searchTerm, datePreset, customFrom, customTo]);

  // Summary aggregations
  const summary = useMemo(() => {
    let totalMT = 0, totalRevenue = 0, totalTDS = 0, totalNet = 0;
    filteredBookings.forEach(b => {
      totalMT += parseFloat(b.quantity || 0);
      totalRevenue += parseFloat(b.total_amount || 0);
      totalTDS += parseFloat(b.tds_amount || 0);
      totalNet += parseFloat(b.net_payable || b.total_amount || 0);
    });
    return { totalMT, totalRevenue, totalTDS, totalNet, count: filteredBookings.length };
  }, [filteredBookings]);

  // Buyer-wise breakdown
  const buyerBreakdown = useMemo(() => {
    const map = {};
    filteredBookings.forEach(b => {
      const key = b.buyer_email || '—';
      if (!map[key]) map[key] = { buyer: key, orders: 0, mt: 0, revenue: 0, tds: 0, net: 0 };
      map[key].orders += 1;
      map[key].mt += parseFloat(b.quantity || 0);
      map[key].revenue += parseFloat(b.total_amount || 0);
      map[key].tds += parseFloat(b.tds_amount || 0);
      map[key].net += parseFloat(b.net_payable || b.total_amount || 0);
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filteredBookings]);

  // Grade-wise breakdown
  const gradeBreakdown = useMemo(() => {
    const map = {};
    filteredBookings.forEach(b => {
      const key = b.grade || '—';
      if (!map[key]) map[key] = { grade: key, orders: 0, mt: 0, revenue: 0 };
      map[key].orders += 1;
      map[key].mt += parseFloat(b.quantity || 0);
      map[key].revenue += parseFloat(b.total_amount || 0);
    });
    return Object.values(map).sort((a, b) => b.mt - a.mt);
  }, [filteredBookings]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1f2937' }}>Reports Center</h2>
          <p className="text-sm mt-0.5" style={{ color: '#6b7280' }}>
            {loading ? 'Loading…' : `${summary.count} order${summary.count !== 1 ? 's' : ''} in selected range`}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
          <Icon name="BarChart2" size={15} color="#d97706" />
          QM Analytics
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <DateRangeFilter
          preset={datePreset}
          customFrom={customFrom}
          customTo={customTo}
          onPresetChange={setDatePreset}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        <div className="relative">
          <Icon name="Search" size={15} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search buyer email, factory, grade…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-amber-300"
            style={{ borderColor: '#fde68a', color: '#1f2937' }}
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-3" />
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading reports…</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard icon="Package" label="Total MT Sold" value={fmtMT(summary.totalMT)} sub={`${summary.count} orders`} color="#d97706" />
            <SummaryCard icon="TrendingUp" label="Gross Revenue" value={fmt(summary.totalRevenue)} color="#059669" />
            <SummaryCard icon="Percent" label="Total TDS (194Q)" value={fmt(summary.totalTDS)} color="#dc2626" />
            <SummaryCard icon="DollarSign" label="Net Receivable" value={fmt(summary.totalNet)} color="#1e4a72" />
          </div>

          {/* Buyer-wise Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border: '1px solid #fde68a' }}>
            <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: '#fde68a', background: '#fef3c7' }}>
              <Icon name="Users" size={16} color="#92400e" />
              <h3 className="text-sm font-bold" style={{ color: '#92400e' }}>Buyer-wise Breakdown</h3>
            </div>
            {buyerBreakdown.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm" style={{ color: '#9ca3af' }}>No data for selected range</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#fffbeb' }}>
                      {['Buyer Email', 'Orders', 'MT Sold', 'Gross Revenue', 'TDS (194Q)', 'Net Receivable'].map(col => (
                        <th key={col} className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: '#92400e', borderBottom: '1px solid #fde68a' }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buyerBreakdown.map((row, idx) => (
                      <tr key={row.buyer} style={{ background: idx % 2 === 0 ? '#ffffff' : '#fffdf7', borderBottom: '1px solid #fef3c7' }}>
                        <td className="px-4 py-3 font-medium text-xs" style={{ color: '#1e4a72' }}>{row.buyer}</td>
                        <td className="px-4 py-3 text-center font-semibold" style={{ color: '#374151' }}>{row.orders}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: '#374151' }}>{fmtMT(row.mt)}</td>
                        <td className="px-4 py-3 font-semibold" style={{ color: '#059669' }}>{fmt(row.revenue)}</td>
                        <td className="px-4 py-3 font-semibold" style={{ color: '#dc2626' }}>{fmt(row.tds)}</td>
                        <td className="px-4 py-3 font-semibold" style={{ color: '#1e4a72' }}>{fmt(row.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#fef3c7', borderTop: '2px solid #fde68a' }}>
                      <td className="px-4 py-3 text-xs font-bold" style={{ color: '#92400e' }}>TOTAL</td>
                      <td className="px-4 py-3 text-center font-bold" style={{ color: '#92400e' }}>{summary.count}</td>
                      <td className="px-4 py-3 font-bold" style={{ color: '#92400e' }}>{fmtMT(summary.totalMT)}</td>
                      <td className="px-4 py-3 font-bold" style={{ color: '#92400e' }}>{fmt(summary.totalRevenue)}</td>
                      <td className="px-4 py-3 font-bold" style={{ color: '#92400e' }}>{fmt(summary.totalTDS)}</td>
                      <td className="px-4 py-3 font-bold" style={{ color: '#92400e' }}>{fmt(summary.totalNet)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Grade-wise Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border: '1px solid #fde68a' }}>
            <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: '#fde68a', background: '#fef3c7' }}>
              <Icon name="Tag" size={16} color="#92400e" />
              <h3 className="text-sm font-bold" style={{ color: '#92400e' }}>Grade-wise Breakdown</h3>
            </div>
            {gradeBreakdown.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm" style={{ color: '#9ca3af' }}>No data for selected range</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#fffbeb' }}>
                      {['Grade', 'Orders', 'MT Sold', 'Gross Revenue'].map(col => (
                        <th key={col} className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: '#92400e', borderBottom: '1px solid #fde68a' }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gradeBreakdown.map((row, idx) => (
                      <tr key={row.grade} style={{ background: idx % 2 === 0 ? '#ffffff' : '#fffdf7', borderBottom: '1px solid #fef3c7' }}>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#fef3c7', color: '#92400e' }}>{row.grade}</span>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold" style={{ color: '#374151' }}>{row.orders}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: '#374151' }}>{fmtMT(row.mt)}</td>
                        <td className="px-4 py-3 font-semibold" style={{ color: '#059669' }}>{fmt(row.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsCenterQMTab;
